import { FC, useCallback, useMemo } from 'react';
import { Flex } from 'antd';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { Dayjs } from 'dayjs';
import { DataObjectDescriptionVersionStore } from '../DataObjectDescriptionVersionStore';
import { useSystemClassifierItemsBy } from '../../configuration';
import { Frequency } from '../types';
import { getDayjs, getDayjsNow } from '../../common/utils';
import {
  ActionButton,
  Detail,
  getPrevPeriodEnd,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Padding,
} from '../../common/components';
import {
  confirmPanelFormClose,
  Form,
  FormAlerts,
  FormField,
} from '../../common/form';
import { ValidationRuleTestRunValidFormSchema } from '../schema/ValidationRuleTestRunValidFormSchema';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import {
  ValidationRuleTestValidationType,
  ValidationRuleValidDataType,
} from '../schema';
import { isDisabledDate } from '../valueDate/utils';
import translations from './ValidationRuleTestRunModal.translations.json';
import validationTranslations from '../translations/validation.translations.json';
import { useDataObjectVersionsQuery } from '../query/useDataObjectVersionsQuery';
import { keepPreviousData } from '@tanstack/react-query';
import { FormDataObjectVersionSelect } from '../../common/form/input/FormDataObjectVersionSelect';
import { FormPeriodEndPicker } from '../../common/form/input/FormPeriodEndPicker';

const { addTranslations, t } = i18nNsFactory('validation-rule-test-run-modal');
addTranslations(translations);
addTranslations(validationTranslations);

export interface ValidationRuleTestRunModalProps {
  validationRule?: Partial<ValidationRuleValidDataType>;
  onClose: () => void;
}

export const ValidationRuleTestRunModal: FC<ValidationRuleTestRunModalProps> = (
  props,
) => {
  const { validationRule, onClose } = props;

  const [dataObjectDescriptionVersion] =
    DataObjectDescriptionVersionStore.useValue('dataObjectDescriptionVersion');
  const [openTestValidate] =
    DataObjectDescriptionVersionStore.useValue('openTestValidate');

  const systemClassifierItemsById = useSystemClassifierItemsBy(['FREQUENCY']);
  const frequencyCode = (systemClassifierItemsById['FREQUENCY'].get(
    dataObjectDescriptionVersion?.frequencyId ?? 0,
  )?.code ?? 'WEEK') as Frequency;

  const validPeriod = [
    getDayjs(dataObjectDescriptionVersion?.validPeriod[0]),
    getPrevPeriodEnd(getDayjsNow(), frequencyCode),
  ] as [Dayjs | null, Dayjs | null];

  const handleSubmit = useCallback(
    (params: ValidationRuleTestValidationType) => {
      onClose();
      openTestValidate?.(params);
    },
    [onClose, openTestValidate],
  );

  const defaultValues = useMemo(
    () => ({
      validationRuleId: validationRule?.id,
    }),
    [validationRule?.id],
  );

  return (
    <Form
      schema={ValidationRuleTestRunValidFormSchema}
      defaultValues={defaultValues}
      onSubmit={handleSubmit}
      idAttributePrefix="validationTestRun"
    >
      <ValidationRuleTestRunModalInner
        validationRule={validationRule}
        validPeriod={validPeriod}
        frequency={frequencyCode}
        onClose={onClose}
      />
    </Form>
  );
};

function ValidationRuleTestRunModalInner({
  validationRule,
  validPeriod,
  frequency,
  onClose,
}: {
  validationRule?: Partial<ValidationRuleValidDataType>;
  validPeriod: [Dayjs | null, Dayjs | null];
  frequency: Frequency;
  onClose?: () => void;
}) {
  const { setValue } = useFormContext();
  const { isDirty } = useFormState();
  const { valueDate } = useWatch();
  const handleCancel = useCallback(() => {
    if (isDirty) {
      confirmPanelFormClose(onClose);
    } else {
      onClose?.();
    }
  }, [isDirty, onClose]);

  const [dataObjectDescriptionVersion] =
    DataObjectDescriptionVersionStore.useValue('dataObjectDescriptionVersion');

  const { data: versions, isFetching: isFetchingVersions } =
    useDataObjectVersionsQuery(
      {
        id: Number(dataObjectDescriptionVersion.dataObjectId),
        valueDate: valueDate ? valueDate : undefined,
      },
      {
        enabled: !!dataObjectDescriptionVersion.dataObjectId && !!valueDate,
        placeholderData: keepPreviousData,
      },
    );

  return (
    <Modal
      footer={
        <ModalFooter>
          <Flex justify="end" gap="small">
            <ActionButton
              type="abort"
              onClick={handleCancel}
              label={t('button.cancel')}
            />
            <ActionButton type="formSubmit" label={t('button.run')} />
          </Flex>
        </ModalFooter>
      }
      onCancel={onClose}
      width="30rem"
    >
      <ModalHeader>
        <ModalTitle>{t(`heading`)}</ModalTitle>
      </ModalHeader>
      <FormAlerts />
      <Padding scrollable>
        <Flex vertical gap="middle">
          <Detail label={t('fields.validationRule.label')}>
            {validationRule?.code}
          </Detail>
          <FormField
            name="valueDate"
            size="small"
            label={t('fields.valueDate.label')}
            required
          >
            <FormPeriodEndPicker
              name="valueDate"
              frequency={frequency}
              disabledDate={(date: Dayjs) => {
                const correctedDate =
                  frequency === 'MONTH'
                    ? date.endOf('month').startOf('day')
                    : date;
                return isDisabledDate(correctedDate, [validPeriod]);
              }}
              onChange={() => setValue('outputDataVersion', undefined)}
            />
          </FormField>
          <FormField
            name="outputDataVersionId"
            size="small"
            label={t('fields.outputDataVersion.label')}
            required
          >
            <FormDataObjectVersionSelect
              name="outputDataVersionId"
              versions={versions || []}
              isLoading={isFetchingVersions}
              disabled={!versions || versions.length === 0}
            />
          </FormField>
        </Flex>
      </Padding>
    </Modal>
  );
}
